<script setup lang="ts">
import NotificationsPanelHeader from '~/components/marketing/notifications/notifications-panel-header.vue';
import { useNotifications } from '~/composables/useStates/useNotifications';
import NotificationsTabsDivider from '~/components/marketing/notifications/notifications-tabs-divider.vue';
import NotificationsNotification from '~/components/marketing/notifications/notifications-notification.vue';
import NotificationsNotificationSkeleton from '~/components/marketing/notifications/notifications-notification-skeleton.vue';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';
import type { TNotificationType } from '~/interfaces/notifications/notifications';

const {
  activeTab,
  availableNotifications,
  error,
  loading,
  notificationsState,
  paginateBackward,
  paginateForward,
  fetchNotificationsOnOpen,
} = useNotifications();

const emits = defineEmits<{ (e: 'close'): void }>();
const activeId = ref<string | null>(null);

const windowHeight = computed(() => window?.innerHeight);

const notificationContainerHeight = computed(() =>
  availableNotifications.value.length > 1
    ? windowHeight.value - 192
    : windowHeight.value - 128,
);

const handleTabChange = (tab: TNotificationType) => {
  nextTick(() => {
    activeTab.value = tab;
    document
      .getElementById(`notification-tab-${tab.toLowerCase()}`)
      ?.scrollTo({ top: 0, behavior: 'smooth' });
  });
};

onMounted(fetchNotificationsOnOpen);
</script>

<template>
  <DesignSystemTabs
    stretch
    header-container-class="py-1 sticky top-[4.5rem]"
    header-inner-class="overflow-x-scroll"
    tabs-content-class="no-scrollbar"
    default-tab="All"
    tab-button-classes="text-xs"
    @tab-change="handleTabChange"
  >
    <template #header>
      <NotificationsPanelHeader @close="emits('close')" />
    </template>
    <template #divider>
      <NotificationsTabsDivider
        v-if="
          availableNotifications.some((tab) => tab.notifications.length > 0) ||
          notificationsState.hasDateFilter
        "
      />
    </template>
    <template #tabList>
      <DesignSystemTabsTab
        v-for="(tab, i) in availableNotifications"
        :id="tab.type"
        :key="i"
        :title="`<div class='flex flex-col gap-1 items-center justify-center'>
                <span>${tab.type}</span>
                <span class='rounded-full text-light-50 bg-primary-blue-gradient w-6 text-xxs'>
                    ${tab.unread}
                </span>
                </div>`"
        :class-binding="[`h-[${notificationContainerHeight}px] no-scrollbar`]"
      >
        <div
          :id="`notification-tab-${tab.type.toLowerCase()}`"
          class="px-4 overflow-y-scroll bg-layer-secondary no-scrollbar"
          :style="{ height: `${notificationContainerHeight}px` }"
        >
          <div
            v-if="!loading && !error"
            class="h-full flex flex-col gap-2 py-2"
          >
            <div v-if="tab.notifications.length === 0">
              <UserInterfaceGenericError state="info">
                <span v-html="$t('no-notifications')" />
              </UserInterfaceGenericError>
            </div>
            <NotificationsNotification
              v-for="(notification, j) in tab.notifications"
              :key="j"
              :notification="notification"
              :active="notification.notificationId === activeId"
              @active="activeId = $event.id"
            />
            <div
              v-if="
                availableNotifications.length < notificationsState.total &&
                activeTab === 'All'
              "
              class="flex items-center justify-center py-2 pb-4 gap-4"
            >
              <DesignSystemButton
                v-if="notificationsState.page > 1"
                type="tertiary-alternative"
                @click="
                  paginateBackward(`notification-tab-${tab.type.toLowerCase()}`)
                "
              >
                <ChevronLeftIcon />
              </DesignSystemButton>
              <DesignSystemButton
                v-if="
                  notificationsState.page * notificationsState.limit <
                  notificationsState.total
                "
                type="tertiary-alternative"
                @click="
                  paginateForward(`notification-tab-${tab.type.toLowerCase()}`)
                "
              >
                <ChevronRightIcon />
              </DesignSystemButton>
            </div>
          </div>
          <NotificationsNotificationSkeleton v-else-if="loading && !error" />
          <div v-else>
            <UserInterfaceGenericError state="error">
              <span v-html="$t(error)" />
            </UserInterfaceGenericError>
          </div>
        </div>
      </DesignSystemTabsTab>
    </template>
  </DesignSystemTabs>
</template>
